/* eslint-disable react-hooks/exhaustive-deps */
import './style.scss';

import { InputNumber } from 'antd';
import * as duration from 'duration-fns';
import Slider from 'rc-slider';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { TfiTarget } from 'react-icons/tfi';
import { useDispatch, useSelector } from 'react-redux';

import { createPlayer, findClosestToMidnight } from '../../components/timeline/helpers';
import { ModeEnum } from '../../core/ui/enums/ModeEnum';
import { PlaybackEnum } from '../../core/ui/enums/PlaybackEnum';
import { convertMsToFrames } from '../../helpers/convertMsToFrames';
import { getProjectDuration, getScenePlaybackLength } from '../../helpers/timelineUtil';
import { useScaleFactor } from '../../hooks/useScaleFactor';
import { C9ProjectDef } from '../../model/definitions/C9ProjectDef';
import PlayerContext from '../../pages/playground/playerContext/PlayerContext';
import { ActiveDef, setProjectToPlay, setTime } from '../../store/slices/active-slice';
import { RootState } from '../../store/store';
import Player from '../player/Player';
import style from './style.module.scss';

interface PlayerProps {
  toStart: () => void;
  backFive: () => void;
  start: () => void;
  pause: () => void;
  stop: () => void;
  skipFive: () => void;
  toEnd: () => void;
  skip?: (e: number) => void;
  fullScreen?: boolean;
  canvas?: boolean;
  passRef?: React.RefObject<HTMLDivElement>;
  onClose?(): void;
  prevScene: (e: number) => void;
  nextScene: (e: number) => void;

  enforceFullScreen?: boolean;
}

const readableTime = (time: number, canvas: boolean) => {
  const normal = duration.normalize(Math.round(time ? time : 0));
  return ` ${String('0' + normal.hours).slice(-2)}:${String('0' + normal.minutes).slice(
    -2,
  )}:${String('0' + normal.seconds).slice(-2)}${!canvas ? ';' : ''}`;
};
const SecondaryControls: React.FC<PlayerProps> = ({
  toStart,
  backFive,
  start,
  pause,
  stop,
  skipFive,
  toEnd,
  skip,
  fullScreen = false,
  canvas,
  passRef,
  onClose,
  prevScene,
  nextScene,
  enforceFullScreen,
}) => {
  const { activeFramerate, mode, activeScene, syncSpace, activeMap, projectToPlay } = useSelector<
    RootState,
    ActiveDef
  >((state) => state.active);
  const dispatch = useDispatch();
  const [currentTime, setCurrentTime] = useState<string>();
  const contextValue = useContext(PlayerContext);
  const project = useSelector<RootState>((state) => state.project.present.project) as C9ProjectDef;
  const scene = project.sceneDefs.find((scene) => scene.id === activeScene);
  const [idle, setIdle] = useState(true);
  const scaleFactor = useScaleFactor(fullScreen);
  const [hovering, setHovering] = useState(false);
  const [currentMap, setCurrentMap] = useState(activeMap);
  // const { localTime, utcTime, timeZone } = useTimeUpdater(contextValue, scene);
  const space = scene?.mapPanels.find((map) => map.id === currentMap)?.wdSpace[0];
  const spaceId = space?.id;
  useEffect(() => {
    activeMap && setCurrentMap(activeMap);
  }, [activeMap]);

  const times = space?.indicator;
  useEffect(() => {
    setCurrentTime(readableTime(contextValue.time, !!canvas));
  }, [contextValue.time]);
  const fullTime = () => {
    const scene = project.sceneDefs.find((scene) => scene.id === (activeScene as string));
    if (mode === ModeEnum.SEQUENCE && scene) return getScenePlaybackLength(scene);
    else return getProjectDuration(projectToPlay);
  };
  const controlsRef = useRef<HTMLDivElement>(null);
  const timerId = useRef<NodeJS.Timer>();
  useEffect(() => {
    project &&
      dispatch(
        setProjectToPlay({ projectToPlay: createPlayer(project, syncSpace, activeFramerate) }),
      );
  }, [project.videoOver]);
  useEffect(() => {
    const handleMove = () => {
      if (passRef?.current && fullScreen) passRef.current.style.cursor = 'auto';
      if (controlsRef?.current && fullScreen) controlsRef.current.style.display = 'flex';
      clearTimeout(timerId.current);
      timerId.current = undefined;
      setIdle(false);
      clearTimeout(timerId.current);
      timerId.current = setTimeout(() => {
        setIdle(true);
        if (passRef?.current && fullScreen) passRef.current.style.cursor = 'none';
        if (controlsRef?.current && fullScreen) controlsRef.current.style.display = 'none';
      }, 1500);
    };
    fullScreen && window.addEventListener('mousemove', handleMove, true);
    return () => {
      window.removeEventListener('click', handleMove);
    };
  });
  const deltaRef = useRef<HTMLInputElement>(null);

  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    const handleMouseMove = () => {
      setHovering(true);
      document.body.style.cursor = 'default';

      // Clear any existing timeout before setting a new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set timeout to reset the state to false after 3 seconds
      timeoutRef.current = setTimeout(() => {
        if (contextValue.isPlaying === PlaybackEnum.PLAYING) {
          setHovering(false);
          document.body.style.cursor = 'none';
        }
      }, 3000);
    };

    // Add event listener to the window
    window.addEventListener('mousemove', handleMouseMove);

    // Cleanup the event listener and timeout on component unmount
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [contextValue.isPlaying]);
  return (
    <div
      ref={controlsRef}
      className={style.secondaryControls}
      style={{
        justifyContent: fullScreen ? 'center' : 'inherit',
        height: enforceFullScreen ? 75 : canvas ? 'auto' : 75,
        outline: 'none',
        //transform: `scale(${scaleInverse})`,
        zIndex: fullScreen || enforceFullScreen ? 9999 : '',
        transform: `scale(${fullScreen ? 1 / scaleFactor : 1})`,
        visibility: hovering ? 'visible' : 'hidden',
      }}
      tabIndex={0}
      id="secondary-controls"
    >
      <div
        className={`flex justify-center items-center ${canvas && style.secondaryControlsCanvas} ${
          canvas && idle && !hovering && ''
        }`}
        style={{
          zIndex: fullScreen ? 999 : 'auto',
        }}
      >
        <Player
          exitfullscreen={onClose}
          project={project}
          toStart={toStart}
          backFive={backFive}
          onPlay={start}
          onPause={pause}
          onStop={stop}
          skipFive={skipFive}
          toEnd={toEnd}
          prevScene={prevScene}
          nextScene={nextScene}
        />
        {canvas && (
          <div className={style.lowerPlayer}>
            <div className={`${style.numberContainer} flex gap-2`}>{currentTime}</div>
            <Slider
              min={0}
              max={
                mode === ModeEnum.SEQUENCE
                  ? project.sceneDefs.find((scene) => scene.id === activeScene)?.durationInMS
                  : project
                  ? getProjectDuration(projectToPlay)
                  : 0
              }
              value={contextValue.time}
              //handleRender={(origin) => origin}
              className={style.slider}
              onChange={(e) => {
                typeof e === 'number' && contextValue.setTime(e);
                typeof e === 'number' &&
                  contextValue.isPlaying === PlaybackEnum.PLAYING &&
                  skip &&
                  skip(e);
                typeof e === 'number' && dispatch(setTime({ activeTime: e }));
              }}
            />
            <div className={style.numberContainer}>
              {readableTime(fullTime() > 0 ? fullTime() : 1, canvas)}
            </div>
          </div>
        )}
        {!canvas && (
          <div className={'flex flex-col items-center'}>
            <div className={'ml-4 flex items-center gap-2'}>
              <div>Δt:</div>
              <InputNumber
                suffix={'h'}
                ref={deltaRef}
                min={times ? findClosestToMidnight(times, 0)?.min : 0}
                max={times ? findClosestToMidnight(times, 0)?.max : 0}
                disabled={
                  !spaceId ||
                  !syncSpace.includes(spaceId) ||
                  contextValue.isPlaying === PlaybackEnum.PLAYING
                }
                key={syncSpace.toString()}
                onChange={(value) => {
                  const time = times
                    ? findClosestToMidnight(times, Number(value) ?? 0)?.value
                    : null;
                  if (time !== null && time !== undefined && !isNaN(time) && time >= 0) {
                    contextValue.setTime(time);
                    dispatch(setTime({ activeTime: time }));
                    if (skip) {
                      skip(time);
                    }
                  }
                }}
              />
            </div>
            <button
              className={
                ' flex items-center justify-around ml-auto mt-1 bg-green-700 w-2/3 p-1 rounded'
              }
              onClick={() => {
                const value = deltaRef.current?.value;
                const time = times ? findClosestToMidnight(times, Number(value) ?? 0)?.value : null;
                time && contextValue.setTime(time);
                time && dispatch(setTime({ activeTime: time }));
                if (skip) {
                  time && skip(time);
                }
              }}
            >
              <TfiTarget /> return
            </button>
          </div>
        )}
      </div>
      {!canvas && (
        <div className={`${style.timeDisplay} pl-[10px]`}>
          <div>
            <span>
              {currentTime}
              {(convertMsToFrames(activeFramerate, contextValue.time) % activeFramerate)
                .toString()
                .padStart(2, '0')}
            </span>
            /
            <span>
              {readableTime(
                mode === ModeEnum.SEQUENCE
                  ? project?.sceneDefs?.find((scene) => scene.id === activeScene)?.durationInMS ?? 0
                  : getProjectDuration(projectToPlay) ?? 0,
                !!canvas,
              )}
              {(
                convertMsToFrames(
                  activeFramerate,
                  mode === ModeEnum.SEQUENCE
                    ? project?.sceneDefs?.find((scene) => scene.id === activeScene)?.durationInMS ??
                        0
                    : getProjectDuration(projectToPlay) ?? 0,
                ) % activeFramerate
              )
                .toString()
                .padStart(2, '0')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecondaryControls;
